/**
 * SessionExpiredModal -- WP-011 Story 1 (AC-001, AC-002, AC-003) + Story 2 (AC-004, AC-005)
 *
 * Non-dismissable full-screen dialog overlay that renders when AuthContext
 * transitions to unauthenticated with reason "session_expired".
 *
 * Mount condition (AC-001):
 *   - Mounts ONLY when unauthenticatedReason === "session_expired"
 *   - Does NOT mount for "manual_sign_out" (deliberate logout) or undefined
 *   - App shell is made inert (pointer-events:none) via AppShell in App.tsx
 *
 * Non-dismissable constraints (AC-002):
 *   - Backdrop click: stopPropagation; modal stays visible
 *   - ESC key: capture-phase handler calls preventDefault+stopPropagation
 *   - All non-Tab keypresses: stopPropagation blocks app-level handlers
 *
 * Exact copy (AC-003):
 * Heading: Your session has expired
 * Supporting line: Please sign in again to continue
 * CTA button: Sign in again -> navigates to /login, modal unmounts
 *
 * State preservation (Story 2 / AC-004, AC-005):
 *   - useLayoutEffect saves attic_return_path and attic_form_draft to
 *     sessionStorage when isVisible transitions to true (before browser paint).
 *   - captureActiveDraft() reads the registered form getter if any form is
 *     currently mounted; skips write when null (no active form).
 *
 * Accessibility (foundation for AC-006, Story 4):
 *   - role=dialog, aria-modal=true, aria-labelledby -> heading id
 *   - Programmatic focus to CTA on mount
 *   - Focus trap: Tab cycles within modal card (single focusable element)
 *
 * Animations (GPU-composited per ENGINEERING.md):
 *   - sessionScrimIn / sessionCardIn keyframes from globals.css (WP-009)
 */

import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router";
import { useAuth } from "../context/AuthContext";
import { useFormDraftContext } from "../context/FormDraftContext";
import { saveReturnPath, saveFormDraft } from "../utils/sessionState";

const HEADING_ID = "session-expired-modal-heading";

export function SessionExpiredModal() {
  const { unauthenticatedReason, clearExpiredReason } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { captureActiveDraft } = useFormDraftContext();
  const signInBtnRef = useRef<HTMLButtonElement>(null);
  // Guard so we only write sessionStorage once per expiry event.
  const persistedRef = useRef(false);

  const isVisible = unauthenticatedReason === "session_expired";

  // AC-004 + AC-005: persist return path and form draft BEFORE browser paint.
  // useLayoutEffect fires synchronously after DOM mutation, before the user
  // sees the modal -- satisfying the AC requirement that writes happen
  // "before the modal renders" (i.e., before the first visible frame).
  useLayoutEffect(() => {
    if (isVisible && !persistedRef.current) {
      persistedRef.current = true;
      saveReturnPath(location.pathname);
      // captureActiveDraft() returns null when no form is registered; saveFormDraft
      // skips the write for null (AC-005: do NOT write when no form is active).
      saveFormDraft(captureActiveDraft());
    }
    if (!isVisible) {
      // Reset guard when modal is dismissed so a subsequent expiry can save again.
      persistedRef.current = false;
    }
  }, [isVisible, location.pathname, captureActiveDraft]);

  // AC-006 prep: programmatic focus to CTA button when modal appears
  useEffect(() => {
    if (isVisible) {
      const id = requestAnimationFrame(() => signInBtnRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [isVisible]);

  // AC-002: capture-phase keydown handler keeps modal non-dismissable.
  // AC-006 prep: Tab trapped -- single focusable element stays focused.
  useEffect(() => {
    if (!isVisible) return undefined;

    function handleKeyDown(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        // AC-002: ESC must NOT dismiss the modal.
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (e.key === "Tab") {
        // Single focusable element -- keep focus on Sign in again button.
        e.preventDefault();
        signInBtnRef.current?.focus();
        return;
      }
      // All other keypresses: stop propagation (AC-002).
      e.stopPropagation();
    }

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [isVisible]);

  const handleSignIn = useCallback(() => {
    clearExpiredReason();
    navigate("/login");
  }, [clearExpiredReason, navigate]);

  // AC-002: scrim click must NOT dismiss the modal
  const handleScrimClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    signInBtnRef.current?.focus();
  }, []);

  // AC-002: card click must not bubble to scrim (belt-and-suspenders)
  const handleCardClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  }, []);

  if (!isVisible) return null;

  return (
    <div
      data-testid="session-expired-modal-scrim"
      role="dialog"
      aria-modal="true"
      aria-labelledby={HEADING_ID}
      aria-describedby="session-expired-modal-body"
      onClick={handleScrimClick}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(17, 24, 39, 0.72)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 600,
        padding: "var(--space-6, 1.5rem)",
        animation: "sessionScrimIn 0.2s ease-out both",
      }}
    >
      <div
        data-testid="session-expired-modal-card"
        onClick={handleCardClick}
        style={{
          background: "var(--color-surface)",
          borderRadius: "var(--radius-xl, 16px)",
          boxShadow: "var(--shadow-modal)", /* warm token — no cool-tone fallback (AC-005) */
          padding: "var(--space-8, 2rem)",
          width: "min(400px, 90vw)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "var(--space-6, 1.5rem)",
          textAlign: "center",
          animation: "sessionCardIn 0.22s ease-out 0.02s both",
        }}
      >
        {/* Lock icon -- decorative, aria-hidden */}
        <div
          aria-hidden="true"
          style={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: "var(--color-error-surface)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--color-error)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            data-testid="session-expired-modal-icon"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 9.9-1" />
          </svg>
        </div>

        {/* Heading + body -- AC-003 exact copy required */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-2, 0.5rem)",
          }}
        >
          <h2
            id={HEADING_ID}
            data-testid="session-expired-modal-heading"
            style={{
              fontSize: 'var(--fs-xl)',
              fontWeight: 700,
              color: "var(--color-text-primary)",
              lineHeight: "28px",
              margin: 0,
            }}
          >
            Your session has expired
          </h2>

          <p
            id="session-expired-modal-body"
            data-testid="session-expired-modal-message"
            style={{
              fontSize: 'var(--fs-base)',
              color: "var(--color-text-muted)",
              lineHeight: "22px",
              margin: 0,
            }}
          >
            Please sign in again to continue
          </p>
        </div>

        {/* AC-003: CTA MUST read exactly Sign in again; navigates to /login */}
        <button
          ref={signInBtnRef}
          data-testid="session-expired-modal-signin-btn"
          type="button"
          onClick={handleSignIn}
          style={{
            width: "100%",
            minHeight: 48,
            fontSize: 'var(--fs-md)',
            fontWeight: 600,
            letterSpacing: "0.01em",
            borderRadius: "var(--radius-md, 8px)",
            border: "none",
            background: "var(--color-secondary)",
            color: "var(--color-text-on-primary)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background 0.15s ease, transform 0.1s ease",
           
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "var(--color-secondary)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "var(--color-secondary)"; }}
          onFocus={(e) => { e.currentTarget.style.boxShadow = "0 0 0 3px rgba(198, 93, 60, 0.3)"; }}
          onBlur={(e) => { e.currentTarget.style.boxShadow = "none"; }}
          onMouseDown={(e) => { e.currentTarget.style.transform = "scale(0.98)"; }}
          onMouseUp={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
        >
          Sign in again
        </button>
      </div>
    </div>
  );
}
